/**
 * Het tabblad Lessen: de lessen van deze leerkracht, een nieuwe les
 * aanmaken (onderdelen/LesFormulier.js), en per les:
 *
 *   het oog         het voorbeeld van tekst en toets (onderdelen/LesVoorbeeld.js)
 *   het tandwiel    de leerlingen in de les en hun afwijkingen
 *                   (onderdelen/LeerlingInstellingen.js)
 *   ✓ / ✗           de les aan- of uitzetten voor de leerlingen
 *   de prullenbak   de les verwijderen, met alle resultaten erin
 *
 * Een nieuwe les opent meteen in het voorbeeld. De groep en de instellingen
 * blijven daarna in het formulier staan, voor de volgende les.
 */
import React, { useEffect, useState } from 'react';
import { Check, Eye, Plus, Settings, Trash2, X } from 'lucide-react';
import { leerkrachtApi as api } from '../../../api/leerkracht';
import { LeerlingInstellingen } from '../onderdelen/LeerlingInstellingen';
import { LesFormulier, NIEUWE_LES, lesVoorServer } from '../onderdelen/LesFormulier';
import { LesVoorbeeld } from '../onderdelen/LesVoorbeeld';

export function Lessen({ actief, meldFout }) {
  const [lessen, setLessen] = useState([]);
  const [groepen, setGroepen] = useState([]);
  const [teksten, setTeksten] = useState([]);
  const [laden, setLaden] = useState(false);

  const [formulierOpen, setFormulierOpen] = useState(false);
  const [nieuw, setNieuw] = useState(NIEUWE_LES);
  const [aanmaken, setAanmaken] = useState(false);

  const [voorbeeldId, setVoorbeeldId] = useState(null);   // de les in het voorbeeld
  const [instellingen, setInstellingen] = useState(null);  // de les waarvan de leerlingen open zijn

  useEffect(() => {
    if (!actief) return;
    const open = async () => {
      setLaden(true);
      try {
        setLessen(await api.haalLessen());
        setGroepen(await api.haalGroepen());
        setTeksten(await api.haalTeksten());
      } catch (err) {
        meldFout(err.message);
      } finally {
        setLaden(false);
      }
    };
    open();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen van het tabblad
  }, [actief]);

  const herlaad = async () => {
    const nieuweLijst = await api.haalLessen();
    setLessen(nieuweLijst);
    return nieuweLijst;
  };

  const kiesGroep = async (groepId) => {
    setNieuw(prev => ({ ...prev, class_id: groepId }));
    if (!groepId) return;
    try {
      const standaard = await api.haalStandaarden(groepId);
      setNieuw(prev => ({
        ...prev,
        class_id: groepId,
        illustratie_stijl: standaard.illustratie_stijl || 'schattig',
      }));
    } catch (err) {
      console.error('Fout bij laden standaarden:', err);
    }
  };

  const maakAan = async () => {
    setAanmaken(true);
    try {
      const gemaakt = await api.maakLes(lesVoorServer(nieuw, teksten.find(t => t.id === nieuw.text_id)));
      await herlaad();
      setFormulierOpen(false);
      setNieuw(prev => ({ ...prev, text_id: '', naam: '' }));
      setVoorbeeldId(gemaakt.id);
    } catch (err) {
      meldFout(err.message);
    } finally {
      setAanmaken(false);
    }
  };

  const zetAanOfUit = async (id) => {
    try {
      await api.zetLesAanOfUit(id);
      await herlaad();
    } catch (err) {
      meldFout(err.message);
    }
  };

  const verwijder = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze les wilt verwijderen?')) return;
    try {
      await api.verwijderLes(id);
      await herlaad();
    } catch (err) {
      meldFout(err.message);
    }
  };

  if (laden) return <p>Laden...</p>;

  const voorbeeld = lessen.find(l => l.id === voorbeeldId);

  return (
    <div className="card">
      <div className="flex-between mb-16">
        <h2>Lessen</h2>
        <button className="btn btn-primary" onClick={() => setFormulierOpen(!formulierOpen)}>
          <Plus size={16} /> Nieuwe les
        </button>
      </div>

      {formulierOpen && (
        <LesFormulier les={nieuw} wijzig={setNieuw} teksten={teksten} groepen={groepen} onKiesGroep={kiesGroep}
          bezig={aanmaken} onAanmaken={maakAan} onAnnuleer={() => setFormulierOpen(false)} />
      )}

      {voorbeeld && (
        <LesVoorbeeld les={voorbeeld} onSluit={() => setVoorbeeldId(null)} onAanUit={() => zetAanOfUit(voorbeeld.id)} meldFout={meldFout} />
      )}

      {instellingen ? (
        <LeerlingInstellingen les={instellingen} onTerug={() => setInstellingen(null)} meldFout={meldFout} />
      ) : (
        <table>
          <thead>
            <tr>
              <th>Les</th>
              <th>Tekst</th>
              <th>Groep</th>
              <th>Instellingen</th>
              <th>Voortgang</th>
              <th>Status</th>
              <th>Acties</th>
            </tr>
          </thead>
          <tbody>
            {lessen.map(les => (
              <tr key={les.id}>
                <td>{les.naam}</td>
                <td>{les.text_titel}</td>
                <td>{les.class_naam}</td>
                <td>
                  <span className="text-muted" style={{ fontSize: '12px' }}>
                    {les.vorm === 'dakpan'
                      ? <><span className="badge badge-primary">Groepsles</span>{les.verteld_deel ? ` | u vertelt deel ${les.verteld_deel}` : ''}</>
                      : <>CR:{les.close_reading_aantal} | E:{les.evaluatie_aantal}</>}
                    {les.illustratie_stijl && ` | ${les.illustratie_stijl}`}
                  </span>
                </td>
                <td>{les.completed_count} / {les.student_count}</td>
                <td>
                  <span className={`badge ${les.is_active ? 'badge-success' : 'badge-warning'}`}>{les.is_active ? 'Actief' : 'Inactief'}</span>
                  {les.tekst_klaar === false && (
                    <span className="badge badge-warning" style={{ marginLeft: 6 }}
                      title="De beheerder maakt de tekst opnieuw op elk niveau; tot dan kan de les niet aan, en wie nog niet begon, ziet hem niet.">
                      Tekst niet klaar
                    </span>
                  )}
                </td>
                <td>
                  <div className="flex gap-4">
                    <button className="btn btn-info" style={{ padding: '6px 12px', background: 'var(--info)', color: 'var(--wit)' }}
                      onClick={() => setVoorbeeldId(les.id)} title={les.vorm === 'dakpan' ? 'Preview tekst en groepjes' : 'Preview tekst en toets'}>
                      <Eye size={14} />
                    </button>
                    <button className="btn btn-primary" style={{ padding: '6px 12px' }} onClick={() => setInstellingen(les)} title="Bekijk leerlingen">
                      <Settings size={14} />
                    </button>
                    <button className={`btn ${les.is_active ? 'btn-secondary' : 'btn-success'}`} style={{ padding: '6px 12px' }}
                      onClick={() => zetAanOfUit(les.id)} title={les.is_active ? 'Deactiveren' : 'Activeren'}>
                      {les.is_active ? <X size={14} /> : <Check size={14} />}
                    </button>
                    <button className="btn btn-danger" style={{ padding: '6px 12px' }} onClick={() => verwijder(les.id)} title="Verwijderen">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
